import React, { useState, useCallback } from 'react';
import { Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { getInt } from 'utils/functions';
import DocumentPartnerExtraInformationComponent from './DocumentPartnerExtraInformationComponent';
import { UPDATE_PARTNER_EXTRA_INFO } from './mutations';
import { PARTNER_EXTRA_INFO_QUERY } from './queries';
import { DOCUMENT_PARTNER_EXTRA_INFORMATION_UPDATED, DOCUMENT_PARTNER_EXTRA_INFORMATION_INVALIDATED } from './subscriptions';
import { withTranslation } from 'react-i18next';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useMutation, useApolloClient } from '@apollo/client';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useSimpleError } from 'customHooks/useSimpleError';
import { withRights } from 'providers/RightsConsumer';
import { emptyArray } from 'constants/emptyConst';
import { testMobilePhone } from 'utils/simple-validations';
import { useSelector, shallowEqual } from 'react-redux';
import { loadPropByCode } from 'features/offer/edit/assets/asset/actionCreators';
import { useToast } from 'utils/hooks';
import { useDispatch } from 'react-redux';
import { showError as showErrorWithSnackBar } from 'actions/actionCreators/snackbarActions';

const DocumentPartnerExtraInformationContainer = ({ partnerId, documentId, siteId, t, rights, clientExtraInformationWrite }) => {
  const client = useApolloClient();
  const [partnerExtraInfos, setPartnerExtraInfos] = useState(emptyArray);
  const [saveDisabled, setSaveDisabled] = useState([]);
  const [saving, setSaving] = useState([]);
  const [savedWithMandatoryPropertiesEmpty, setSavedWithMandatoryExtraInfoEmpty] = useState(false);
  const countryCode = useSelector(state => state.params.items.countryCode, shallowEqual);
  const addToast = useToast();
  const showError = useSimpleError();
  const dispatch = useDispatch();

  const loadPropertyDetails = useCallback(
    propCode => {
      return loadPropByCode(propCode)
        .then(data => {
          return data;
        })
        .catch(error => {
          dispatch(showErrorWithSnackBar(error.message, error.correlationId));
          throw error;
        });
    },
    [dispatch]
  );

  const { loading } = useQueryWithErrorHandling(PARTNER_EXTRA_INFO_QUERY, {
    variables: { documentId, siteId, partnerId },
    skip: getInt(partnerId) === null || getInt(documentId) === null || getInt(siteId) === null,
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const sections = data.partnerExtraInfos.data.sections.map(section => {
        let values = section.jsonData ? JSON.parse(section.jsonData) : null;
        if (section.display === 'Grid') {
          values = values ?? [
            section.columns.reduce((defaultValues, column) => {
              defaultValues[column.propertyName] = null;
              return defaultValues;
            }, {})
          ];
        }
        return { ...section, values };
      });

      const result = {
        data: { ...data.partnerExtraInfos.data, sections: sections },
        partnerId: data.partnerExtraInfos.partnerId
      };
      setPartnerExtraInfos(result);
      setSaveDisabled(sections.map(() => true));
      setSaving(sections.map(() => false));
    }
  });

  const [updatePartnerExtraInfoSection] = useMutation(UPDATE_PARTNER_EXTRA_INFO, {
    onError: err => {
      showError(err);
      setSaveDisabled(prevState => prevState.map(() => false));
      setSaving(prevState => prevState.map(() => false));
    }
  });

  useGlobalSubscription(DOCUMENT_PARTNER_EXTRA_INFORMATION_UPDATED, {
    variables: {
      documentId,
      siteId,
      partnerId
    },
    skip: getInt(partnerId) === null || getInt(documentId) === null || getInt(siteId) === null,
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setPartnerExtraInfos(previousState => {
        const { sections } = previousState.data;
        const updatedSection = data.partnerExtraInfoUpdated;

        const sectionIndex = sections.findIndex(
          section => section.extraInformationId === updatedSection.extraInformationId || section.name === updatedSection.sectionName
        );

        if (sectionIndex !== -1) {
          sections[sectionIndex].extraInformationId = updatedSection.extraInformationId;
          sections[sectionIndex].jsonData = updatedSection.jsonData;
          sections[sectionIndex].values = JSON.parse(updatedSection.jsonData);
        } else if (updatedSection.jsonData !== null) {
          sections.push({
            extraInformationId: updatedSection.extraInformationId,
            jsonData: updatedSection.jsonData,
            values: JSON.parse(updatedSection.jsonData),
            name: updatedSection.sectionName
          });
        }

        return { ...previousState, data: { sections } };
      });
      addToast(t('OfferEdit.Client.ExtraInfo.ExtraInformationUpdated'), 'success');
      setSaving(prevState => prevState.map(() => false));
    }
  });

  useGlobalSubscription(DOCUMENT_PARTNER_EXTRA_INFORMATION_INVALIDATED, {
    variables: { documentId, siteId, partnerId },
    skip: getInt(partnerId) === null || getInt(documentId) === null || getInt(siteId) === null,
    onSubscriptionData: ({
      subscriptionData: {
        data: { partnerExtraInfoInvalidated }
      }
    }) => {
      if (!partnerExtraInfoInvalidated) return;
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'partnerExtraInfos' });
      client.cache.gc();
      setPartnerExtraInfos(emptyArray);
    }
  });

  const validateExtraInformation = useCallback(
    sections => {
      return sections.some(
        section =>
          section.values &&
          section.values.some(value =>
            section.columns.some(column => {
              if (column.required && !value[column.propertyName]) return true;
              if (column.propertyType === 'PHONE_NUMBER') {
                return value[column.propertyName] && !testMobilePhone(String(value[column.propertyName]), countryCode);
              }
              return false;
            })
          )
      );
    },
    [countryCode]
  );

  const handleSavePartnerExtraInfoSection = useCallback(
    sectionIndex => {
      const section = partnerExtraInfos.data.sections[sectionIndex];
      if (validateExtraInformation([section])) {
        setSavedWithMandatoryExtraInfoEmpty(true);
        return;
      }

      updatePartnerExtraInfoSection({
        variables: {
          input: {
            documentId: documentId,
            siteId: siteId,
            partnerId: partnerId,
            sectionName: section.name,
            extraInformationId: section.extraInformationId,
            jsonData: section.values && section.values.length > 0 ? JSON.stringify(section.values) : null
          }
        }
      });
      setSaving(prevState => {
        const newSaving = [...prevState];
        newSaving[sectionIndex] = true;
        return newSaving;
      });
      setSaveDisabled(prevState => {
        const newSaveDisabled = [...prevState];
        newSaveDisabled[sectionIndex] = true;
        return newSaveDisabled;
      });
    },
    [updatePartnerExtraInfoSection, partnerExtraInfos, documentId, siteId, partnerId, validateExtraInformation]
  );

  const handlePropertyChange = (index, columnName, newValue, sectionIdx) => {
    setPartnerExtraInfos(prevState => {
      const { data } = prevState;
      const { sections } = data;

      if (sections[sectionIdx]) {
        const { values } = sections[sectionIdx];

        if (values[index]) {
          values[index][columnName] = newValue;
        }
      }
      return { ...prevState };
    });
    setSaveDisabled(prevState => {
      const newSaveDisabled = [...prevState];
      newSaveDisabled[sectionIdx] = false;
      return newSaveDisabled;
    });
  };

  const handleAddExtraInfo = index => {
    setPartnerExtraInfos(prevState => {
      const { data } = prevState;
      const { sections } = data;
      const { columns } = sections[index];

      if (sections[index]) {
        const { values } = sections[index];
        const localValues = values ?? [];
        const newObject = {};
        columns.forEach(column => {
          newObject[column.propertyName] = null;
        });
        localValues.push(newObject);
        sections[index].values = localValues;
      }

      return { ...prevState };
    });
    setSaveDisabled(prevState => {
      const newSaveDisabled = [...prevState];
      newSaveDisabled[index] = false;
      return newSaveDisabled;
    });
  };

  const handleDeleteRow = (rowIndex, sectionIdx) => {
    setPartnerExtraInfos(prevState => {
      const { data } = prevState;
      const { sections } = data;

      if (sections[sectionIdx]) {
        const { values } = sections[sectionIdx];
        values.splice(rowIndex, 1);
      }
      return { ...prevState };
    });
    setSaveDisabled(prevState => {
      const newSaveDisabled = [...prevState];
      newSaveDisabled[sectionIdx] = false;
      return newSaveDisabled;
    });
  };

  return (
    <Grid container>
      <DocumentPartnerExtraInformationComponent
        data={partnerExtraInfos.data}
        t={t}
        onPropertyChange={handlePropertyChange}
        rights={rights}
        clientExtraInformationWrite={clientExtraInformationWrite}
        onDeleteRow={handleDeleteRow}
        onAddExtraInfo={handleAddExtraInfo}
        onSavePartnerExtraInfos={handleSavePartnerExtraInfoSection}
        countryCode={countryCode}
        saveDisabled={saveDisabled}
        validateExtraInformation={validateExtraInformation}
        savedWithMandatoryPropertiesEmpty={savedWithMandatoryPropertiesEmpty}
        loading={loading}
        partnerId={partnerId}
        saving={saving}
        loadPropertyDetails={loadPropertyDetails}
      />
    </Grid>
  );
};

DocumentPartnerExtraInformationContainer.propTypes = {
  partnerId: PropTypes.number,
  t: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  clientExtraInformationWrite: PropTypes.bool.isRequired
};

export default withTranslation()(withRights(DocumentPartnerExtraInformationContainer));
